import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  HStack,
  Stack,
  InputGroup,
  InputLeftElement,
  Grid,
  GridItem,
  Box,
  useTheme,
  Tag,
  TagLabel,
  Divider,
  VStack,
  Center,
  FormHelperText,
  Textarea,
  Button as ChakraButton,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { extractCodeFromStorage } from 'hooks/useCodeStorage';
import { useFigmaDownload } from 'hooks/useFigmaDownload';
import { useToastWrapper } from 'hooks/useToastWrapper';
import html2canvas from 'html2canvas';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import { getGeneratedPageURL } from 'components/DevSandbox/DevSandboxProvider';
import { CODE_FREE_LIMIT, CODE_PAID_LIMIT } from 'components/DevSandbox/constants';
import Icon from 'components/Icon';
import Link from 'components/Link';
import { UnlockFigmaModal } from 'components/Modal/UnlockFigmaModal';
import { ProtectedElement } from 'components/ProtectedElement';
import Text from 'components/Text';
import ToggleCodePrivacySwitch from 'components/ToggleCodePrivacySwitch';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import {
  Project,
  EdgeFunctionName,
  SubmitChecks,
  SubmitCodeParams,
  SubmitCodeResponse,
} from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';
import { pluralize } from 'utils/strings';

interface IBubbleCounterProps {
  count: number;
}

const BubbleCounter = ({ count }: IBubbleCounterProps) => {
  return (
    <Box width="24px" height="24px" backgroundColor="purple.400" borderRadius="50%">
      <Center>
        {count}
        <Text></Text>
      </Center>
    </Box>
  );
};

interface SubmitSolutionForm {
  title: string;
  repository_url?: string;
  live_preview_url?: string;
  feedback: string;
}

const validationSchema = yup
  .object({
    title: yup
      .string()
      .required('Title is required')
      .max(80, 'Title is too long (maximum is 80 characters)'),
    feedback: yup.string().max(300, 'Feedback is too long (maximum is 300 characters)'),
  })
  .required();

const validationSchemaExternalSubmission = yup
  .object({
    title: yup
      .string()
      .required('Title is required')
      .max(80, 'Title is too long (maximum is 80 characters)'),
    repository_url: yup
      .string()
      .url('Repository URL should be a valid URL')
      .max(255, 'Repository URL is too long (maximum is 255 characters)')
      .test(
        'is-repository-url',
        'Repository URL should be from Glitch (https://glitch.com/edit/#!/[MY-GLITCH]), replit (https://replit.com/[@ME]/[REPL]) or GitHub (https://github.com/[ME]/[MY-REPO]).',
        (value) => {
          return ['glitch.com/edit/#!', 'replit.com', 'github.com'].some((codeRepository) =>
            (value?.toLowerCase() || '').includes(codeRepository)
          );
        }
      ),
    live_preview_url: yup
      .string()
      .url('Live Preview URL should be a valid URL')
      .max(255, 'Live Preview URL is too long (maximum is 255 characters)')
      .test(
        'is-live-preview-url',
        'Live Preview URL should be from Glitch (https://[MY-GLITCH].glitch.me), replit (https://[REPL].[ME].repl.co), Vercel (https://[MY-APP].vercel.app), Netlify (https://[MY-APP].netlify.app), Firebase (https://[MY-APP].web.app or https://[MY-APP].firebaseapp.com), AWS Amplify (https://[BRANCH].[MY-APP].amplifyapp.com), GitHub Pages (https://[ME].github.io/[MY-REPO]) or Cloudflare Pages (https://[NAME].pages.dev).',
        (value) => {
          return [
            'glitch.me',
            'repl.co',
            'vercel.app',
            'netlify.app',
            'web.app',
            'firebaseapp.com',
            'amplifyapp.com',
            'github.io',
            'pages.dev',
          ].some((codeRepository) => (value?.toLowerCase() || '').includes(codeRepository));
        }
      ),
  })
  .required();

interface ISubmitSolutionProps {
  project: Project;
}

const SubmitSolution = ({
  project: { id, screen_name, title: projectTitle, solution_title, repository_url, preview_url },
}: ISubmitSolutionProps) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isViaGithub = searchParams.get('via') === 'github';
  const [isPrivate, setIsPrivate] = useState(false);
  const [activeGithubTab, setActiveGithubTab] = useState<'get-started' | 'submit'>('get-started');

  const [checks, setChecks] = useState<SubmitChecks | null>(null);
  const [all_checks_passed, setAllChecksPassed] = useState(false);

  useEffect(() => {
    setChecks(null);
    setAllChecksPassed(false);
  }, [isViaGithub]);

  const { id: userId, screenName: userScreenName, isFree } = useCurrentUser();

  const { isDownloadingFigmaDesign, onFigmaDownload } = useFigmaDownload({
    screenName: screen_name,
    type: 'project',
    entityId: id,
  });
  const figmaModal = useDisclosure();

  const storedHtml = extractCodeFromStorage({
    type: 'project',
    screenName: screen_name,
    userId,
    code: 'html',
  });
  const storedCss = extractCodeFromStorage({
    type: 'project',
    screenName: screen_name,
    userId,
    code: 'css',
  });
  const storedJs = extractCodeFromStorage({
    type: 'project',
    screenName: screen_name,
    userId,
    code: 'js',
  });

  const previewUrl = useMemo(
    () => getGeneratedPageURL(storedHtml, storedCss, storedJs),
    [storedHtml, storedCss, storedJs]
  );

  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  const { isDesktopNavigation } = useMediaQueries();

  const navigate = useNavigate();

  const supabase = useSupabaseTypedClient();

  const queryClient = useQueryClient();

  const { toastError } = useToastWrapper();

  const { borderRadius } = useTheme();

  const createIframeScreenshot = async (): Promise<string> => {
    const iframe = previewIframeRef.current;
    if (!iframe) return '';

    try {
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDocument) return '';

      const canvas = await html2canvas(iframeDocument.body, {
        useCORS: true,
        allowTaint: true,
        width: 1440,
        height: 1080,
      });

      return canvas.toDataURL('image/png');
    } catch {
      return '';
    }
  };

  const mutation = useMutation<SubmitCodeResponse, string, SubmitCodeParams>({
    mutationFn: supabaseMutationFn((params) =>
      supabase.functions.invoke<SubmitCodeResponse>(EdgeFunctionName.SubmitCode, {
        body: params as SubmitCodeParams,
      })
    ),
    onSuccess: async (data) => {
      setChecks(data.checks as SubmitChecks);
      setAllChecksPassed(data.all_checks_passed);

      if (data.all_checks_passed) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCurrentUser, userId] }),
          queryClient.invalidateQueries({
            queryKey: [TanstackQueryName.GetProject, screen_name],
          }),
          queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetProjects, userId] }),
          queryClient.invalidateQueries({
            queryKey: [TanstackQueryName.GetProjectSolutions, id],
          }),
          queryClient.invalidateQueries({
            queryKey: [TanstackQueryName.GetUserAchievements, userId],
          }),
          queryClient.invalidateQueries({
            queryKey: [TanstackQueryName.GetOnboardingMissions, userId],
          }),
        ]);

        setTimeout(() => {
          navigate(data.solution_url);
        }, 300);
      } else {
        return toastError('Some of the checks failed, try to fix them and submit again.');
      }
    },
    onError: () => {
      toastError(`Can't submit solution, please try again later.`);
    },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm<SubmitSolutionForm>({
    // @ts-ignore
    resolver: yupResolver(isViaGithub ? validationSchemaExternalSubmission : validationSchema),
    mode: 'onSubmit',
    defaultValues: {
      repository_url:
        repository_url.includes('/projects/') && repository_url.includes(userScreenName)
          ? ''
          : repository_url || '',
      live_preview_url:
        preview_url.includes('/projects/') && preview_url.includes(userScreenName)
          ? ''
          : preview_url || '',
      title: solution_title || '',
      feedback: '',
    },
  });

  const title = watch('title');
  const feedback = watch('feedback');
  const repositoryUrl = watch('repository_url');
  const livePreviewUrl = watch('live_preview_url');

  const onSubmit = async (values: SubmitSolutionForm) => {
    const CODE_LIMIT = isFree ? CODE_FREE_LIMIT : CODE_PAID_LIMIT;

    if (storedHtml.length > CODE_LIMIT) {
      return toastError(`Exceeded maximum ${CODE_LIMIT} characters of HTML`);
    }

    if (storedCss.length > CODE_LIMIT) {
      return toastError(`Exceeded maximum ${CODE_LIMIT} characters of CSS`);
    }

    if (storedJs.length > CODE_LIMIT) {
      return toastError(`Exceeded maximum ${CODE_LIMIT} characters of JS`);
    }

    // Include editor settings so viewers can see the solution with the author's config
    const storedEditorSettings = (() => {
      try {
        const raw = localStorage.getItem('bds-editor-settings');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Only include relevant fields (framework, cssMode, packages)
        const { framework, cssMode, packages } = parsed;
        return { framework, cssMode, packages };
      } catch {
        return null;
      }
    })();

    if (!isViaGithub) {
      const iframeScreenshot = await createIframeScreenshot();

      return await mutation.mutate({
        ...values,
        html: storedHtml,
        css: storedCss,
        js: storedJs,
        title,
        feedback,
        is_private: isPrivate,
        type: 'project',
        entityScreenName: screen_name,
        iframe_screenshot: iframeScreenshot,
        editor_settings: storedEditorSettings,
      });
    }

    return await mutation.mutate({
      ...values,
      html: '',
      css: '',
      js: '',
      title,
      feedback,
      is_private: isPrivate,
      type: 'project',
      entityScreenName: screen_name,
      repository_url: repositoryUrl,
      live_preview_url: livePreviewUrl,
      editor_settings: storedEditorSettings,
    });
  };

  const failedChecksCount = Object.values(checks || {}).filter((flag) => flag === false).length;

  return (
    <>
      <VStack spacing={3} alignItems="flex-start">
        <form onSubmit={handleSubmit(onSubmit as any)}>
          <VStack spacing={3} overflowY="auto" h="80vh" alignItems="flex-start">
            <Box backgroundColor="whiteAlpha.200" borderRadius={borderRadius} p={4}>
              <Grid templateColumns="repeat(3, 1fr)" gap={8}>
                <GridItem colSpan={isDesktopNavigation ? 2 : 3}>
                  <Stack spacing={8}>
                    {isViaGithub ? (
                      <HStack spacing={0}>
                        <Box
                          as="button"
                          type="button"
                          px={4}
                          py={2}
                          borderBottom="2px solid"
                          borderColor={activeGithubTab === 'get-started' ? 'purple.400' : 'transparent'}
                          color={activeGithubTab === 'get-started' ? 'white' : 'whiteAlpha.600'}
                          onClick={() => setActiveGithubTab('get-started')}
                          _hover={{ color: 'white' }}
                        >
                          <Text fontWeight="semibold">Get Started</Text>
                        </Box>
                        <Box
                          as="button"
                          type="button"
                          px={4}
                          py={2}
                          borderBottom="2px solid"
                          borderColor={activeGithubTab === 'submit' ? 'purple.400' : 'transparent'}
                          color={activeGithubTab === 'submit' ? 'white' : 'whiteAlpha.600'}
                          onClick={() => setActiveGithubTab('submit')}
                          _hover={{ color: 'white' }}
                        >
                          <Text fontWeight="semibold">Submit</Text>
                        </Box>
                      </HStack>
                    ) : (
                      <Text fontSize="xLarge" fontWeight="semibold" mb={-4}>Submit</Text>
                    )}
                    {isViaGithub && activeGithubTab === 'get-started' && (
                      <>
                        <Box
                          borderRadius="xl"
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          bg="whiteAlpha.50"
                          overflow="hidden"
                        >
                          <Box p={5}>
                            <VStack alignItems="flex-start" spacing={3}>
                              <HStack>
                                <BubbleCounter count={1} />
                                <Text fontSize="xLarge">Get the Design</Text>
                              </HStack>
                              <Text opacity="secondary" fontWeight="regular">
                                Download the Figma design to see exact specifications, colors, and
                                spacing. Review the project cards for feature requirements.
                              </Text>
                              <Button
                                scheme="outline"
                                size="sm"
                                borderColor="purple.400"
                                isLoading={isDownloadingFigmaDesign}
                                loadingText="Downloading..."
                                onClick={() => {
                                  if (isFree) {
                                    figmaModal.onOpen();
                                    return;
                                  }
                                  onFigmaDownload();
                                }}
                                leftIcon={<Icon type="FigmaLogo" size={16} />}
                              >
                                {isFree ? 'Upgrade to BIG' : 'Download Figma Design'}
                              </Button>
                            </VStack>
                          </Box>
                          <Divider borderColor="whiteAlpha.200" />
                          <Box p={5}>
                            <VStack alignItems="flex-start" spacing={3}>
                              <HStack>
                                <BubbleCounter count={2} />
                                <Text fontSize="xLarge">Use the GitHub Template</Text>
                              </HStack>
                              <Text opacity="secondary" fontWeight="regular">
                                Clone or fork our starter template to get the project structure and
                                boilerplate ready.
                              </Text>
                              <Button
                                scheme="outline"
                                size="sm"
                                borderColor="orange.400"
                                href={`https://github.com/elise-bigdevsoon/big-dev-soon-${screen_name}`}
                                leftIcon={<Icon type="Github" size={16} />}
                              >
                                GitHub Template
                              </Button>
                            </VStack>
                          </Box>
                          <Divider borderColor="whiteAlpha.200" />
                          <Box p={5}>
                            <VStack alignItems="flex-start" spacing={3}>
                              <HStack>
                                <BubbleCounter count={3} />
                                <Text fontSize="xLarge">Build & Deploy</Text>
                              </HStack>
                              <Text opacity="secondary" fontWeight="regular">
                                Implement the design, deploy to a hosting provider (Vercel, Netlify,
                                GitHub Pages, etc.), and come back here to submit your solution.
                              </Text>
                              <ChakraButton
                                size="sm"
                                variant="outline"
                                color="white"
                                borderColor="green.400"
                                _hover={{ bg: 'whiteAlpha.100' }}
                                onClick={() => setActiveGithubTab('submit')}
                                rightIcon={<Icon type="BackArrow" />}
                                sx={{ '& svg': { transform: 'rotate(180deg)' } }}
                              >
                                <Text fontWeight="semibold">Ready to Submit</Text>
                              </ChakraButton>
                            </VStack>
                          </Box>
                        </Box>
                        <Box
                          p={4}
                          borderRadius={borderRadius}
                          border="1px solid"
                          borderColor="whiteAlpha.200"
                          background="linear-gradient(135deg, rgba(159, 122, 234, 0.08) 0%, rgba(237, 137, 54, 0.08) 100%)"
                        >
                          <HStack spacing={3} flexWrap="wrap">
                            <Box
                              p={2}
                              borderRadius="md"
                              bg="whiteAlpha.100"
                            >
                              <Icon type="SubmitCode" />
                            </Box>
                            <VStack alignItems="flex-start" spacing={1} flex={1} minW="200px">
                              <Text fontWeight="semibold">
                                Want to use our built-in Code Editor with AI?
                              </Text>
                              <Text fontSize="small" opacity="secondary" fontWeight="regular">
                                No setup needed — write code, preview, and submit directly in the
                                browser.
                              </Text>
                            </VStack>
                            <ChakraButton
                              variant="outline"
                              size="sm"
                              color="white"
                              borderColor="whiteAlpha.400"
                              _hover={{ bg: 'whiteAlpha.100' }}
                              onClick={() => navigate(pathname.replace(/\/submit$/, ''), { replace: true })}
                              flexShrink={0}
                            >
                              <Text fontWeight="semibold">Switch to Editor</Text>
                            </ChakraButton>
                          </HStack>
                        </Box>
                      </>
                    )}
                    {(!isViaGithub || activeGithubTab === 'submit') && (
                      <Box
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        bg="whiteAlpha.50"
                        overflow="hidden"
                      >
                        <Box p={5}>
                          <FormControl isInvalid={Boolean(errors.title)}>
                            <FormLabel htmlFor="title">
                              <VStack alignItems="flex-start">
                                <HStack>
                                  <BubbleCounter count={1} />
                                  <Text fontSize="xLarge">Solution title</Text>
                                </HStack>
                                <Text opacity="secondary">
                                  Try to use the title to easily identify your project solution.
                                </Text>
                              </VStack>
                            </FormLabel>
                            <Input
                              {...register('title')}
                              placeholder={`e.g. My solution to ${projectTitle} project`}
                              variant="filled"
                              maxLength={80}
                              autoFocus
                              autoComplete="off"
                            />
                            <HStack justifyContent="space-between" alignItems="center">
                              {errors.title?.message ? (
                                <FormErrorMessage>{errors.title.message}</FormErrorMessage>
                              ) : (
                                <div />
                              )}
                              <FormHelperText>
                                <HStack spacing={1}>
                                  <Text fontSize="micro" opacity="secondary" fontWeight="regular">
                                    Characters remaining:
                                  </Text>
                                  <Text fontSize="micro" opacity="secondary" fontWeight="semibold">
                                    {80 - title.length}
                                  </Text>
                                </HStack>
                              </FormHelperText>
                            </HStack>
                          </FormControl>
                        </Box>
                        {isViaGithub && (
                          <>
                            <Divider borderColor="whiteAlpha.200" />
                            <Box p={5}>
                              <FormControl isInvalid={Boolean(errors.repository_url)}>
                                <FormLabel htmlFor="repository_url">
                                  <VStack alignItems="flex-start">
                                    <HStack>
                                      <BubbleCounter count={2} />
                                      <Text fontSize="xLarge">Repository URL</Text>
                                    </HStack>
                                    <Text opacity="secondary">
                                      We accept URLs from{' '}
                                      <Link href="https://glitch.com/">Glitch</Link>,{' '}
                                      <Link href="https://replit.com/">replit</Link>, and{' '}
                                      <Link href="https://github.com/">GitHub</Link>.
                                    </Text>
                                  </VStack>
                                </FormLabel>
                                <InputGroup>
                                  <InputLeftElement pointerEvents="none">
                                    <Icon type="Website" />
                                  </InputLeftElement>
                                  <Input
                                    {...register('repository_url')}
                                    placeholder={`e.g. https://glitch.com/edit/#!/my-glitch-app`}
                                    variant="filled"
                                    autoComplete="off"
                                  />
                                </InputGroup>
                                <FormErrorMessage>{errors.repository_url?.message}</FormErrorMessage>
                              </FormControl>
                            </Box>
                            <Divider borderColor="whiteAlpha.200" />
                            <Box p={5}>
                              <FormControl isInvalid={Boolean(errors.live_preview_url)}>
                                <FormLabel htmlFor="live_preview_url">
                                  <VStack alignItems="flex-start">
                                    <HStack>
                                      <BubbleCounter count={3} />
                                      <Text fontSize="xLarge">Live Preview URL</Text>
                                    </HStack>
                                    <Text opacity="secondary">
                                      We support <Link href="https://glitch.com/">Glitch</Link>,{' '}
                                      <Link href="https://replit.com/">replit</Link>,{' '}
                                      <Link href="https://vercel.com/">Vercel</Link>,{' '}
                                      <Link href="https://www.netlify.com/">Netlify</Link>,{' '}
                                      <Link href="https://firebase.google.com/">Firebase</Link>,{' '}
                                      <Link href="https://aws.amazon.com/amplify/">
                                        AWS Amplify
                                      </Link>
                                      ,{' '}
                                      <Link href="https://pages.github.com/">GitHub Pages</Link> and{' '}
                                      <Link href="https://pages.cloudflare.com/">
                                        Cloudflare Pages
                                      </Link>
                                      .
                                    </Text>
                                  </VStack>
                                </FormLabel>
                                <InputGroup>
                                  <InputLeftElement pointerEvents="none">
                                    <Icon type="Website" />
                                  </InputLeftElement>
                                  <Input
                                    {...register('live_preview_url')}
                                    placeholder={`e.g. https://my-glitch-app.glitch.me`}
                                    variant="filled"
                                    autoComplete="off"
                                  />
                                </InputGroup>
                                <FormErrorMessage>
                                  {errors.live_preview_url?.message}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          </>
                        )}
                        {!isViaGithub && (
                          <>
                            <Divider borderColor="whiteAlpha.200" />
                            <Box p={5}>
                              <VStack alignItems="flex-start">
                                <HStack>
                                  <BubbleCounter count={2} />
                                  <Text fontSize="xLarge">Your solution</Text>
                                </HStack>
                                <Text opacity="secondary">Preview before submitting.</Text>
                                <Box
                                  width="100%"
                                  height="50vh"
                                  borderRadius={borderRadius}
                                  overflow="hidden"
                                  border="1px solid"
                                  borderColor="whiteAlpha.200"
                                >
                                  <iframe
                                    ref={previewIframeRef}
                                    src={previewUrl}
                                    title="Solution preview"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      border: 'none',
                                      backgroundColor: 'white',
                                    }}
                                    sandbox="allow-scripts allow-same-origin"
                                  />
                                </Box>
                              </VStack>
                            </Box>
                          </>
                        )}
                        <Divider borderColor="whiteAlpha.200" />
                        <Box p={5}>
                          <FormControl isInvalid={Boolean(errors.feedback)}>
                            <FormLabel htmlFor="feedback">
                              <VStack alignItems="flex-start">
                                <HStack>
                                  <BubbleCounter count={isViaGithub ? 4 : 3} />
                                  <Text fontSize="xLarge">Ask for feedback (optional)</Text>
                                </HStack>
                                <Text opacity="secondary">
                                  {`Other people will be able to comment your solution based on this feedback request.`}
                                </Text>
                              </VStack>
                            </FormLabel>
                            <Textarea
                              {...register('feedback')}
                              placeholder="Please share the short brief of your solution and try to ask precisely
                      about the feedback you are looking for. If the message is on point,
                      answers will be too!"
                              variant="filled"
                              maxLength={300}
                              rows={5}
                              autoComplete="off"
                            />
                            <HStack justifyContent="space-between" alignItems="center">
                              {errors.feedback?.message ? (
                                <FormErrorMessage>{errors.feedback.message}</FormErrorMessage>
                              ) : (
                                <div />
                              )}
                              <FormHelperText>
                                <HStack spacing={1}>
                                  <Text fontSize="micro" opacity="secondary" fontWeight="regular">
                                    Characters remaining:
                                  </Text>
                                  <Text fontSize="micro" opacity="secondary" fontWeight="semibold">
                                    {300 - feedback.length}
                                  </Text>
                                </HStack>
                              </FormHelperText>
                            </HStack>
                          </FormControl>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </GridItem>
                <GridItem colSpan={isDesktopNavigation ? 1 : 3}>
                  <HStack height="100%" alignItems="flex-start">
                    {isDesktopNavigation && (
                      <Divider orientation="vertical" width="1px" height="100%" />
                    )}
                    <VStack
                      ml={isDesktopNavigation ? 4 : 0}
                      spacing={8}
                      alignItems="flex-start"
                      width="100%"
                    >
                      <Text fontSize="xLarge">Submit checks</Text>
                      {failedChecksCount > 0 && !mutation.isPending && (
                        <Tag
                          p={2}
                          borderRadius="999px"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          backgroundColor="whiteAlpha.200"
                        >
                          <TagLabel>
                            <HStack>
                              <Icon type="SubmitCheckFailed" />
                              <Text fontSize="large">
                                {failedChecksCount} {pluralize('check', failedChecksCount)} failed
                              </Text>
                            </HStack>
                          </TagLabel>
                        </Tag>
                      )}
                      {isViaGithub && (
                        <VStack alignItems="flex-start">
                          <HStack>
                            <Icon
                              type={
                                !checks
                                  ? 'SubmitCheck'
                                  : mutation.isPending
                                  ? 'SubmitCheck'
                                  : checks?.repository_url_exists
                                  ? 'SubmitCheckPassed'
                                  : 'SubmitCheckFailed'
                              }
                            />
                            <Text fontSize="large">Repository URL exists</Text>
                          </HStack>
                          <Text fontWeight="regular" opacity="secondary">
                            URL must exist and be accessible.
                          </Text>
                        </VStack>
                      )}
                      {isViaGithub && (
                        <VStack alignItems="flex-start">
                          <HStack>
                            <Icon
                              type={
                                !checks
                                  ? 'SubmitCheck'
                                  : mutation.isPending
                                  ? 'SubmitCheck'
                                  : checks?.live_preview_url_exists
                                  ? 'SubmitCheckPassed'
                                  : 'SubmitCheckFailed'
                              }
                            />
                            <Text fontSize="large">Live Preview URL exists</Text>
                          </HStack>
                          <Text fontWeight="regular" opacity="secondary">
                            URL must exists and be accessible.
                          </Text>
                        </VStack>
                      )}
                      {isViaGithub && (
                        <VStack alignItems="flex-start">
                          <HStack>
                            <Icon
                              type={
                                !checks
                                  ? 'SubmitCheck'
                                  : mutation.isPending
                                  ? 'SubmitCheck'
                                  : checks?.live_preview_url_unique
                                  ? 'SubmitCheckPassed'
                                  : 'SubmitCheckFailed'
                              }
                            />
                            <Text fontSize="large">Live Preview URL is unique</Text>
                          </HStack>
                          <Text fontWeight="regular" opacity="secondary">
                            URL must be unique and not already used by another user.
                          </Text>
                        </VStack>
                      )}
                      <VStack alignItems="flex-start">
                        <HStack>
                          <Icon
                            type={
                              !checks
                                ? 'SubmitCheck'
                                : mutation.isPending
                                ? 'SubmitCheck'
                                : checks?.html_tag_exists
                                ? 'SubmitCheckPassed'
                                : 'SubmitCheckFailed'
                            }
                          />
                          <Text fontSize="large">
                            {isViaGithub ? 'index.html exists' : 'HTML tag exists'}
                          </Text>
                        </HStack>
                        <Text fontWeight="regular" opacity="secondary">
                          {isViaGithub
                            ? 'An index.html file must be present in the Live Preview URL.'
                            : 'Your code must contain an <html> tag.'}
                        </Text>
                      </VStack>
                      <VStack alignItems="flex-start">
                        <HStack>
                          <Icon
                            type={
                              !checks
                                ? 'SubmitCheck'
                                : mutation.isPending
                                ? 'SubmitCheck'
                                : checks?.bds_tag_exists
                                ? 'SubmitCheckPassed'
                                : 'SubmitCheckFailed'
                            }
                          />
                          <Text fontSize="large">{`<bds />`} tag exists</Text>
                        </HStack>
                        <Text fontWeight="regular" opacity="secondary">
                          {isViaGithub
                            ? `The special <bds /> HTML tag must be present in the index.html.`
                            : `Your code must contain the special <bds /> HTML tag.`}
                        </Text>
                      </VStack>
                      <ToggleCodePrivacySwitch
                        type="project"
                        isNotCreated
                        togglePrivacyCodeParams={{
                          entityId: '',
                          isPrivate: false,
                          invalidateKeys: [],
                          queryKey: [],
                        }}
                        onChange={(isPrivate) => setIsPrivate(isPrivate)}
                      />
                      <ProtectedElement
                        scheme="gradient"
                        isLoading={mutation.isPending}
                        leftIcon={<Icon type="SubmitCode" />}
                        isDisabled={
                          isViaGithub ? !title || !repositoryUrl || !livePreviewUrl : !title
                        }
                        type="submit"
                        size="lg"
                        fullWidth
                      >
                        <Text>{checks && !all_checks_passed ? 'Retry' : 'Submit Solution'}</Text>
                      </ProtectedElement>
                    </VStack>
                  </HStack>
                </GridItem>
              </Grid>
            </Box>
          </VStack>
        </form>
      </VStack>
      <UnlockFigmaModal
        isOpen={figmaModal.isOpen}
        onClose={figmaModal.onClose}
        isDownloading={isDownloadingFigmaDesign}
        onDownload={onFigmaDownload}
      />
    </>
  );
};

export default SubmitSolution;
